import type { SourceDevice, Stream } from '@bloo-team/api-client'

/**
 * Pure derivations behind Datafabric's home widgets (docs/home.md §Data
 * joins). The home reads are counts so far TODAY (`daily-stream-details`)
 * and the device list (`device-management`) — no rate, no hourly series:
 * those need endpoints the console does not have (CB-36, CB-37).
 */

/** Events ingested today across every stream — the Streams page's footer total. */
export function eventsToday(daily: Record<string, number> | undefined): number | null {
  if (!daily) return null
  return Object.values(daily).reduce((a, n) => a + (Number.isFinite(n) ? n : 0), 0)
}

export interface FlowRow {
  stream: string
  events: number
  /** 0..100 share of the busiest stream. */
  v: number
  /** Known stream, zero events today — the gap this widget exists to show. */
  silent: boolean
}

/**
 * Per-stream flow today: every stream `stream-management` lists, joined to
 * today's count by exact name (the Streams page's `enrich` join). Silent
 * streams sort FIRST — a dead stream is the reason to look — then busiest
 * down. A stream missing from the daily read counts as 0: the endpoint
 * lists only streams that received data.
 */
export function streamFlow(streams: Stream[], daily: Record<string, number>, limit = 6): { rows: FlowRow[]; silent: number } {
  const all = streams.map((s) => ({ stream: s.stream, events: daily[s.stream] ?? 0 }))
  const max = Math.max(0, ...all.map((r) => r.events))
  const rows = all
    .map((r) => ({ ...r, v: max ? Math.round((r.events / max) * 100) : 0, silent: r.events === 0 }))
    .sort((a, b) => Number(b.silent) - Number(a.silent) || b.events - a.events || a.stream.localeCompare(b.stream))
  return { rows: rows.slice(0, limit), silent: rows.filter((r) => r.silent).length }
}

export interface SourceHealth {
  inactive: number
  /** Devices the count was taken over. */
  loaded: number
  /** Server total across every page. */
  total: number
  /** The inactive count covers only the first page. */
  partial: boolean
}

/**
 * Inactive sources over one device page. The endpoint carries exactly
 * `active` / `inactive` (captured 2026-08-21) — no silent/error split, no
 * per-status totals — so a scope bigger than the page reads as partial.
 */
export function sourceHealth(devices: SourceDevice[], total: number): SourceHealth {
  return {
    inactive: devices.filter((d) => d.status === 'inactive').length,
    loaded: devices.length,
    total,
    partial: devices.length < total,
  }
}
